import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Lightbulb, X, HelpCircle } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";

const TipsCtx = createContext(null);
export const tipKey = (pathname) => pathname.replace(/^\//, "").replace(/\/[0-9a-f]{24}(\/|$)/g, "").replace(/\/+$/, "").replace(/\//g, "-") || "portal";

export function TipsProvider({ children }) {
  const { user } = useAuth();
  const [state, setState] = useState(null);
  const load = useCallback(() => { if (user) api.get("/help/tips").then((r) => setState(r.data)).catch(() => {}); }, [user]);
  useEffect(() => { load(); }, [load]);
  const dismiss = async (key) => { setState((s) => s && { ...s, dismissed: [...s.dismissed, key] }); await api.post("/help/tips/dismiss", { key }); };
  const setEnabled = async (enabled) => { setState((s) => s && { ...s, enabled }); await api.put("/help/tips/preferences", { enabled }); };
  const reset = async () => { const { data } = await api.post("/help/tips/reset"); setState((s) => s && { ...s, ...data }); };
  const value = useMemo(() => ({ state, dismiss, setEnabled, reset, reload: load }), [state, load]);
  return <TipsCtx.Provider value={value}>{children}</TipsCtx.Provider>;
}

export const useTips = () => useContext(TipsCtx);

export function PageTip({ dark = false }) {
  const { pathname } = useLocation();
  const tips = useTips();
  const key = tipKey(pathname);
  const text = tips?.state?.enabled && !tips.state.dismissed.includes(key) ? tips.state.tips[key] : null;
  if (!text) return null;
  return (
    <div className={`mb-4 flex items-start gap-2 rounded-xl border px-3 py-2 text-xs animate-in fade-in slide-in-from-top-2 sm:mb-5 sm:gap-3 sm:px-4 sm:py-3 sm:text-sm ${dark ? "border-gold/40 bg-slate-950/60 text-parchment" : "border-gold/40 bg-gold-light/60 text-foreground"}`} role="note" data-testid={`page-tip-${key}`}>
      <Lightbulb size={16} className="mt-0.5 shrink-0 text-gold" />
      <div className="min-w-0 flex-1"><span className="eyebrow mr-2 hidden text-gold sm:inline">Tip</span>{text}</div>
      <div className="ml-auto flex shrink-0 items-center gap-1">
        <Button size="sm" variant="ghost" className="hidden h-7 text-xs sm:inline-flex" onClick={() => tips.setEnabled(false)} data-testid="page-tip-off">Don't show tips</Button>
        <button onClick={() => tips.dismiss(key)} className="flex h-8 w-8 items-center justify-center rounded hover:bg-black/10" aria-label="Dismiss tip" data-testid="page-tip-dismiss"><X size={16} /></button>
      </div>
    </div>
  );
}

export function HelpButton({ dark = false }) {
  const nav = useNavigate();
  const { pathname } = useLocation();
  return (
    <button onClick={() => nav(`/portal/help?from=${encodeURIComponent(tipKey(pathname))}`)} title="Help & tips"
      className={`rounded-lg p-2 transition-colors ${dark ? "text-parchment/70 hover:bg-white/10 hover:text-gold" : "text-crimson/70 hover:bg-crimson/10 hover:text-crimson"}`} data-testid="help-button">
      <HelpCircle size={20} />
    </button>
  );
}
